import { limit, orderBy, where } from "firebase/firestore";
import queryString from "query-string";
import React from "react";
import { Alert, Card, Form, ListGroup } from "react-bootstrap";
import { useNavigate } from "react-router";

import { getUserDetails } from "../../api/api.jsx";
import { useChangelogCollection } from "../../api/firestoreWrappers.generated.jsx";
import Layout, { Title } from "../../components/Layout/Layout.jsx";
import LoadMoreButton from "../../components/LoadMoreButton/index.jsx";
import LogListItem from "../../components/LogListItem/index.jsx";
import LogFilters, {
    COLLECTION_CONFIG,
} from "../../components/LogsFilter/index.jsx";

function LogsList({ logs, filters, setFilters, users, forceSingleColumn }) {
    if (logs.length <= 0) {
        return (
            <Alert variant="primary" className="m-4">
                No recent changes found.
            </Alert>
        );
    }

    return (
        <ListGroup>
            {logs.map((log, index) => (
                <div key={log.id}>
                    <LogListItem
                        log={log}
                        timeDisplayFormat="YYYY-MM-DD HH:mm:ss"
                        filters={filters}
                        setFilters={setFilters}
                        users={users}
                        forceSingleColumn={forceSingleColumn}
                    />
                    {index !== logs.length - 1 && (
                        <div className="border-bottom border-1 border-secondary" />
                    )}
                </div>
            ))}
        </ListGroup>
    );
}

export default function ChangelogPage() {
    const navigate = useNavigate();

    const logsPerPage = 50;
    const [logsToLoad, setLogsToLoad] = React.useState(logsPerPage);
    const [forceSingleColumn, setForceSingleColumn] = React.useState(true);

    const queryParams = queryString.parse(location.search);
    const [filters, setFilters] = React.useState({
        collection: queryParams?.collection ?? "droneConfigs",
        droneId: queryParams?.droneId,
        organizationId: queryParams?.organizationId,
        locationId: queryParams?.locationId,
        routineId: queryParams?.routineId,
        appId: queryParams?.appId,
    });

    function getQueryConstraints(filters, collection) {
        const config = COLLECTION_CONFIG[collection];
        if (!config) return [];

        const reversed = [...config.filters].reverse();
        const match = reversed.find((f) => filters[f.key]);

        if (!match) {
            return [];
        }

        const value = filters[match.key];
        const constraints = [where("docPathSegments", "array-contains", value)];

        return constraints;
    }

    const activeCollection = filters?.collection ?? "droneConfigs";
    const queryConstraints = getQueryConstraints(filters, activeCollection);

    const [allLogs, isLoading] = useChangelogCollection([
        limit(logsToLoad),
        orderBy("changedAt", "desc"),
        where("authId", "not-in", [
            "493740961644-compute@developer.gserviceaccount.com",
            "mission-batch@corvus-backend.iam.gserviceaccount.com",
        ]),
        where("collection", "==", activeCollection),
        ...queryConstraints,
    ]);

    // Some manual filtering for temporary docks
    const logs = React.useMemo(() => {
        if (!allLogs) return [];

        if (activeCollection === "docks") {
            return allLogs.filter((log) => {
                const oldTemporary = log?.diffs?.oldDoc?.temporary === true;
                const newTemporary = log?.diffs?.newDoc?.temporary === true;

                return !oldTemporary && !newTemporary;
            });
        }

        return allLogs;
    }, [allLogs, activeCollection]);

    const [authUserMap, setAuthUserMap] = React.useState({});
    const userCacheRef = React.useRef(new Map());

    React.useEffect(() => {
        if (!logs || logs.length === 0) {
            setAuthUserMap({});
            return;
        }

        let cancelled = false;

        async function loadUsers() {
            try {
                const apiKeyLogs = logs.filter((l) => l.authType === "api_key");
                const authIds = [...new Set(apiKeyLogs.map((l) => l.authId))];

                if (authIds.length === 0) {
                    if (!cancelled) setAuthUserMap({});
                    return;
                }

                const cache = userCacheRef.current;
                const missingIds = authIds.filter((id) => !cache.has(id));

                if (missingIds.length > 0) {
                    const bulkMap = await getUserDetails(missingIds);

                    for (const [uid, user] of Object.entries(bulkMap)) {
                        cache.set(uid, user);
                    }
                }

                if (cancelled) return;

                setAuthUserMap(
                    Object.fromEntries(
                        authIds.map((id) => [id, cache.get(id) ?? null]),
                    ),
                );
            } catch {
                if (!cancelled) {
                    setAuthUserMap({});
                }
            }
        }

        loadUsers();

        return () => {
            cancelled = true;
        };
    }, [logs]);

    React.useEffect(() => {
        const config = COLLECTION_CONFIG[activeCollection];

        const newQuery = { collection: activeCollection };

        config.filters.forEach((f) => {
            if (filters[f.key]) {
                newQuery[f.key] = filters[f.key];
            }
        });

        const nextSearch = queryString.stringify(newQuery);
        const currentParams = new URLSearchParams(location.search);
        const nextParams = new URLSearchParams(nextSearch);

        if (currentParams.toString() !== nextParams.toString()) {
            navigate(`${location.pathname}?${nextParams.toString()}`, {
                replace: true,
            });
        }
    }, [activeCollection, filters, navigate]);

    return (
        <Layout>
            <title>{`Changelogs | MyCorvus`}</title>
            <Card className="mb-4">
                <Title title={"Changelogs"} />
            </Card>

            <div className="d-none d-md-flex">
                <Form.Check
                    type="switch"
                    id="show-single-column-switch"
                    label="Show single column"
                    checked={forceSingleColumn}
                    onChange={(e) => setForceSingleColumn(e.target.checked)}
                />
            </div>
            <LogFilters
                className={"mb-3"}
                filters={filters}
                setFilters={setFilters}
            />

            <Card>
                <LogsList
                    users={authUserMap}
                    logs={logs}
                    filters={filters}
                    setFilters={setFilters}
                    forceSingleColumn={forceSingleColumn}
                />
            </Card>

            <LoadMoreButton
                disabled={logsToLoad > logs.length}
                isLoading={isLoading}
                load={() => {
                    setLogsToLoad(logsToLoad + logsPerPage);
                }}
                showLoadAll={false}
                loadAll={() => {}}
                text={`Showing ${logs.length} logs`}
            />
        </Layout>
    );
}
